import { computed, toValue } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useAccountStore } from 'stores/account'
import { useScanRegistryStore } from 'src/stores/scans/registry'
import { broadcastScanStatusData } from 'src/composables/app/system/useBroadcastChannel'
import {
  refetchHistoryOnScanTypeHandoff,
  syncScanHistoryFromStatus,
} from 'src/composables/scans/query/useScanHistoryCache'
import { scanService } from 'src/services/api/scan.service'
import { QUERY_KEYS, STALE_TIMES } from 'src/services/consts/queries.const'
import { SCAN_STATUS } from 'src/services/consts/shared.const'
import { isActiveScanStatus, isScanInProgress } from 'src/services/helpers/scan/status.helper'

export const SCAN_POLL_INTERVAL_MS = 15000

/**
 * Fetches latest scan result for a site, enrolls active scans, and broadcasts to other tabs.
 *
 * @param {{ queryKey: unknown[], queryClient: import('@tanstack/vue-query').QueryClient, broadcast?: boolean }} context
 * @returns {Promise<import('src/services/helpers/scan/status.helper').ScanStatus|null>}
 */
export async function scanResultQueryFn({ queryKey, queryClient, broadcast = true }) {
  const [, siteIdFromKey] = queryKey
  const siteId = siteIdFromKey?.value ?? siteIdFromKey
  if (!siteId) return null

  const previousScan = queryClient.getQueryData([QUERY_KEYS.SCAN_STATUS, siteId])

  try {
    const scan = await scanService.getLatestScanResult(siteId)

    // 200 + null/empty → no scans yet (empty state). Do not dismiss registry here.
    if (!scan) return null

    const banner = queryClient.getQueryData([QUERY_KEYS.BANNER, siteId])
    useScanRegistryStore().ensureActiveScanTracked({
      siteId,
      siteName: banner?.siteName ?? scan.url ?? 'Banner',
      domain: banner?.url ?? scan.url ?? null,
      scanId: scan.scanId,
      status: scan.status,
      scanType: scan.scanType,
    })

    syncScanHistoryFromStatus(queryClient, siteId, scan)
    refetchHistoryOnScanTypeHandoff(queryClient, siteId, previousScan, scan)

    if (broadcast) {
      broadcastScanStatusData(siteId, scan)
    }

    return scan
  } catch (error) {
    // 404 = site gone — drop drawer enrollment; do not mark the scan FAILED.
    if (error?.response?.status === 404) {
      useScanRegistryStore().dismissScan(siteId)
      throw error
    }

    const cached = queryClient.getQueryData([QUERY_KEYS.SCAN_STATUS, siteId])
    if (cached && isActiveScanStatus(cached.status)) {
      syncScanHistoryFromStatus(queryClient, siteId, {
        ...cached,
        status: SCAN_STATUS.FAILED,
        // History date col uses completedAt for terminal statuses
        completedAt: cached.completedAt || new Date().toISOString(),
      })
    }
    throw error
  }
}

/**
 * Shared TanStack options for `[QUERY_KEYS.SCAN_STATUS, siteId]` observers.
 *
 * Polling (`refetchInterval`) is opt-in via `shouldPoll` so the tab observer and
 * drawer poller can share one cache entry without each scheduling its own timer.
 *
 * @param {{ queryClient: import('@tanstack/vue-query').QueryClient, siteId: import('vue').MaybeRefOrGetter<string>, shouldPoll?: boolean }} input
 */
export function getScanStatusQueryOptions({ queryClient, siteId, shouldPoll = false }) {
  return {
    queryKey: [QUERY_KEYS.SCAN_STATUS, siteId],
    queryFn: (context) => scanResultQueryFn({ ...context, queryClient }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: (query) =>
      isScanInProgress(query.state.data) ? SCAN_POLL_INTERVAL_MS : STALE_TIMES.sm,
    refetchInterval: shouldPoll
      ? (query) => {
          if (query.state.error) return false
          return isScanInProgress(query.state.data) ? SCAN_POLL_INTERVAL_MS : false
        }
      : false,
  }
}

/**
 * Single-site scan result query for the Cookies & Trackers tab.
 *
 * Normalized `ScanStatus.results` exposes classified items as `trackers[]`.
 * Shares `[QUERY_KEYS.SCAN_STATUS, siteId]` with `useScanPollingQueries` (no own
 * interval — live updates come from the drawer poller's shared cache).
 *
 * @param {import('vue').Ref<string|undefined|null>} siteIdRef
 * @param {import('vue').MaybeRefOrGetter<boolean>} [isParentReadyRef] - When false, query stays disabled (default: always ready)
 */
export function useScanResultQuery(siteIdRef, isParentReadyRef = true) {
  const accountStore = useAccountStore()
  const queryClient = useQueryClient()

  const enableQuery = computed(
    () =>
      !!accountStore.accountId &&
      !accountStore.isLoading &&
      !!siteIdRef.value &&
      !!toValue(isParentReadyRef)
  )

  const scanQuery = useQuery({
    ...getScanStatusQueryOptions({ queryClient, siteId: siteIdRef }),
    enabled: enableQuery,
  })

  return {
    scanResult: scanQuery.data,
    isScanLoading: scanQuery.isLoading,
    isScanRefetching: scanQuery.isRefetching,
    scanError: scanQuery.error,
    refetchScan: scanQuery.refetch,
  }
}
